import { useTranslation } from 'react-i18next'

import { FilterMatchMode, FilterOperator, PrimeIcons } from 'primereact/api'
import { Button } from 'primereact/button'
import { Column } from 'primereact/column'
import { DataTable, DataTableFilterMeta } from 'primereact/datatable'
import { InputSwitch } from 'primereact/inputswitch'
import { Message } from 'primereact/message'

import {
  BooleanBodyTemplate,
  BooleanFilterTemplate,
} from '../../../../../../../../components/tableTemplates'
import { HandleableError } from '../../../../../../../../utils/errors/isXiaError'
import { CompanyCode } from '../../../../../suppliers'
import { LoadingScreen } from '../common/LoadingScreen'

type VerifyPanelProps = {
  data?: CompanyCode[]
  deleteExistingCompanyCodes: boolean
  setDeleteExistingCompanyCodes: (value: boolean) => void
  onNext: () => void
  onPrev: () => void
  isLoading: boolean
  error?: HandleableError
}

const defaultFilters: DataTableFilterMeta = {
  global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  IsStandard: {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
  },
  'Common.DisplayId': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
  'Common.Company': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
  'Common.SalesTaxID': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
  'Address.Street': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
  'Address.PostalCode': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
  'Address.City': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
  'Address.Country': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
}

export const VerifyPanel: React.FC<VerifyPanelProps> = ({
  data,
  deleteExistingCompanyCodes,
  setDeleteExistingCompanyCodes,
  onNext,
  onPrev,
  isLoading = false,
  error,
}) => {
  const { t } = useTranslation()

  const Footer = () => {
    return (
      <>
        <div className="flex pt-4 justify-content-between">
          <Button
            label={t(
              'masterDataManagement.companyCodes.table.upload.dialog.verifyPanel.footer.previous',
            )}
            icon={PrimeIcons.ARROW_LEFT}
            onClick={() => onPrev()}
            disabled={isLoading}
          />
          <Button
            label={t(
              'masterDataManagement.companyCodes.table.upload.dialog.verifyPanel.footer.next',
            )}
            icon={PrimeIcons.CHECK}
            onClick={() => onNext()}
            disabled={isLoading || !!error}
          />
        </div>
      </>
    )
  }

  if (isLoading)
    return (
      <>
        <LoadingScreen />
        <Footer />
      </>
    )

  if (error)
    return (
      <>
        <Message
          severity="error"
          className="w-full justify-content-start"
          text={error.message}
        />
        <Footer />
      </>
    )

  return (
    <>
      <div className="flex flex-column gap-4">
        <DataTable
          value={data}
          paginator
          rowsPerPageOptions={[10, 20, 50]}
          rows={10}
          scrollable
          scrollHeight="50vh"
          filters={defaultFilters}
        >
          <Column
            key="IsStandard"
            field="IsStandard"
            header={t(
              'masterDataManagement.companyCodes.table.upload.dialog.verifyPanel.table.columns.isStandard.header',
            )}
            body={(rowData: CompanyCode) =>
              BooleanBodyTemplate(rowData.IsStandard)
            }
            filter
            filterElement={BooleanFilterTemplate}
            sortable
            align="center"
          />
          <Column
            key="Common.DisplayId"
            field="Common.DisplayId"
            header={t(
              'masterDataManagement.companyCodes.table.upload.dialog.verifyPanel.table.columns.displayId.header',
            )}
            filter
            sortable
          />
          <Column
            key="Common.Company"
            field="Common.Company"
            header={t(
              'masterDataManagement.companyCodes.table.upload.dialog.verifyPanel.table.columns.company.header',
            )}
            filter
            sortable
          />
          <Column
            key="Common.SalesTaxID"
            field="Common.SalesTaxID"
            header={t(
              'masterDataManagement.companyCodes.table.upload.dialog.verifyPanel.table.columns.salesTaxID.header',
            )}
            filter
            sortable
          />
          <Column
            key="Address.Street"
            field="Address.Street"
            header={t(
              'masterDataManagement.companyCodes.table.upload.dialog.verifyPanel.table.columns.street.header',
            )}
          />
          <Column
            key="Address.PostalCode"
            field="Address.PostalCode"
            header={t(
              'masterDataManagement.companyCodes.table.upload.dialog.verifyPanel.table.columns.postalCode.header',
            )}
            filter
            sortable
          />
          <Column
            key="Address.City"
            field="Address.City"
            header={t(
              'masterDataManagement.companyCodes.table.upload.dialog.verifyPanel.table.columns.city.header',
            )}
          />
          <Column
            key="Address.Country"
            field="Address.Country"
            header={t(
              'masterDataManagement.companyCodes.table.upload.dialog.verifyPanel.table.columns.country.header',
            )}
            filter
            sortable
          />
        </DataTable>

        <div className="flex gap-2">
          <InputSwitch
            id="delete"
            checked={deleteExistingCompanyCodes}
            onChange={e => setDeleteExistingCompanyCodes(e.value)}
          />
          <label htmlFor="delete">
            {t(
              'masterDataManagement.companyCodes.table.upload.dialog.verifyPanel.deleteSwitch',
            )}
          </label>
        </div>
      </div>
      <Footer />
    </>
  )
}
